import { BulkUploadError } from "../../api/ReportUploadApi";
import validator from "validator";
import {
  isAliasValid,
  isNameValid,
} from "../../reports-wizard/ReportsWizardUtil";

export const COLUMN_VALIDATORS: {
  [key: string]: { validator: (value: string) => string | void };
} = {
  "first name": {
    validator: (value: string) => {
      if (!value) return "First Name is required";
      if (!isNameValid(value)) return "Invalid character in First Name ";
      return;
    },
  },
  "middle name": {
    validator: (value: string) => {
      if (value && !isNameValid(value))
        return "Invalid character in Middle Name";
      return;
    },
  },
  "last name": {
    validator: (value: string) => {
      if (!value) return "Last Name is required";
      if (!isNameValid(value)) return "Invalid character in Last Name";
      return;
    },
  },
  "email addresses": {
    validator: (value: string) => {
      if (!value || validator.isEmpty(value)) return "Email is required";
      if (!validator.isEmail(value)) return "Invalid character in Email";
      return;
    },
  },
  "phone numbers": {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isMobilePhone(value))
        return "Invalid character in Phone Numbers";
      return;
    },
  },
  locations: {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isAscii(value))
        return "Invalid character in Location";
      return;
    },
  },
  employers: {
    validator: (value: string) => {
      if (value && !containsOnlyUnicodeLetters(value))
        return "Invalid character in Employers";
      return;
    },
  },
  degrees: {
    validator: (value: string) => {
      if (value && !containsOnlyUnicodeLetters(value))
        return "Invalid character in Degrees";
      return;
    },
  },
  aliases: {
    validator: (value: string) => {
      if (!value) return;
      const parts = value
        .split(";")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      for (const part of parts) {
        if (!isAliasValid(part)) return "Invalid character in Aliases";
      }
      return;
    },
  },
  "client reference id": {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isAscii(value))
        return "Invalid character in Client Reference ID";
      return;
    },
  },
  "social profiles": {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isURL(value))
        return "Invalid social profile URL";
      return;
    },
  },
  "date of birth": {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isAscii(value))
        return "Invalid character in DOB";
      return;
    },
  },
  "job requisition number": {
    validator: (value: string) => {
      if (!validator.isEmpty(value) && !validator.isAscii(value))
        return "Invalid character in job requisition number";
      return;
    },
  },
};

export function containsOnlyUnicodeLetters(value: string): boolean {
  if (!value) return false;
  const REGEX = /^[\p{L}\p{N}\s\-/\\&|:;]+$/u;
  return REGEX.test(value);
}

async function parseUploadedFile(
  file: File | undefined
): Promise<string[][] | undefined> {
  if (!file) return;
  if (file.type !== "text/csv") return;

  const fileUrl = URL.createObjectURL(file);
  const response = await fetch(fileUrl);
  const text = (await response.text()).replaceAll("\r", "");
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const _data = lines.map((line) => line.split(","));
  return _data;
}

export function validateRow(
  row: string[],
  rowIdx: number,
  columnToKey: {
    [key: string]: string;
  }
): BulkUploadError[] | undefined {
  const validationResult: BulkUploadError[] = [];
  row.forEach((cell, idx) => {
    const colKey = columnToKey[idx];
    const colValidator = COLUMN_VALIDATORS[colKey];
    if (!colValidator) return undefined;
    const message = colValidator.validator(cell);
    if (message) {
      validationResult.push({
        row: rowIdx,
        column: colKey,
        message: message,
      });
    }
  });
  return validationResult;
}

export function buildColumnToKeyMap(header: string[]) {
  const columnToKey: { [key: string]: string } = {};
  for (let i = 0; i < header.length; i++) {
    const column = header[i];
    columnToKey[i] = column.toLowerCase().trim();
  }
  return columnToKey;
}

export function validateDataLocal(
  data: string[][] | undefined
): BulkUploadError[] | undefined {
  if (!data) return;

  const keyToColumn = buildColumnToKeyMap(data[0]);

  const errors: BulkUploadError[] = [];
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    const rowErrors = validateRow(row, i, keyToColumn);
    if (rowErrors) {
      errors.push(...rowErrors);
    }
  }
  return errors;
}

export function validateHeaderColumns(
  data: string[][] | undefined
): BulkUploadError[] | undefined {
  if (!data) return;

  const keyToColumn = buildColumnToKeyMap(data[0]);

  const headerErrors: BulkUploadError[] = [];

  Object.keys(COLUMN_VALIDATORS).forEach((validatorKey) => {
    let found = Object.keys(keyToColumn)
      .map((colIdx) => {
        return keyToColumn[colIdx];
      })
      .some((columnName) => columnName === validatorKey.toLowerCase());
    if (!found) {
      headerErrors.push({
        row: 0,
        column: "all",
        message: `Could not find column ${validatorKey}`,
      });
    }
  });

  if (headerErrors.length > 0) {
    return headerErrors;
  }
  return [];
}

export const parseFile = async (files: File[] | undefined) => {
  if (!files || files.length === 0) return;
  try {
    return await parseUploadedFile(files[0]);
  } catch {
    return;
  }
};
